import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Button,
  Text,
  SimpleGrid,
  Tabs,
  Badge,
  Image,
  IconButton,
} from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi';
import { useDogStore } from '@/stores/dogStore';
import { useAuthStore } from '@/stores/authStore';
import { useVaccinationStore } from '@/stores/vaccinationStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { DogSearchForm } from '@/components/dog/DogSearchForm';
import { DeleteDogDialog } from '@/components/dog/DeleteDogDialog';
import { DogIdentityLabel } from '@/components/dog/DogIdentityLabel';
import { VaccinationAlertSection } from '@/components/vaccination/VaccinationAlertSection';
import { toaster } from '@/components/ui/toaster';
import { Gender, Dog, DogSearchFilter } from '@/types/dog';
import {
  VaccinationAlertLevel,
  getAlertLevelFromDueDate,
  getAlertLabel,
  getAlertColor,
} from '@/utils/vaccinationAlert';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface DogCardWithAIProps {
  dog: Dog;
  onDeleteClick: (dog: Dog) => void;
  alertLevel?: VaccinationAlertLevel;
}

const DogCardWithAI: FC<DogCardWithAIProps> = ({ dog, onDeleteClick, alertLevel }) => {
  const navigate = useNavigate();

  const calculateAge = (): string => {
    const birthDate = dog.birthDate.toDate();
    const today = new Date();
    const ageInYears = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (ageInYears < 1) {
      const ageInMonths = monthDiff < 0 ? 12 + monthDiff : monthDiff;
      return `${ageInMonths}ヶ月`;
    }

    return `${ageInYears}歳`;
  };

  const getGenderColor = (): string => {
    return dog.gender === 'male' ? 'blue' : 'pink';
  };

  const getStatusColor = (): string => {
    switch (dog.status) {
      case 'active':
        return 'green';
      case 'transferred':
        return 'gray';
      case 'deceased':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (): string => {
    switch (dog.status) {
      case 'active':
        return '在籍中';
      case 'transferred':
        return '譲渡済み';
      case 'deceased':
        return '死亡';
      default:
        return '不明';
    }
  };

  return (
    <Box
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      shadow="card"
      transition="all 0.2s"
      _hover={{ shadow: 'cardHover', transform: 'translateY(-2px)' }}
      position="relative"
    >
      {/* サムネイル画像 */}
      <Box
        position="relative"
        w="100%"
        h="200px"
        bg="gray.100"
        cursor="pointer"
        onClick={() => navigate(`/dogs/${dog.id}`)}
      >
        {dog.mainPhotoUrl ? (
          <Image src={dog.mainPhotoUrl} alt={dog.name} w="100%" h="100%" objectFit="cover" />
        ) : (
          <Box
            w="100%"
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.200"
          >
            <Text color="gray.500" fontSize="sm">
              画像なし
            </Text>
          </Box>
        )}

        {/* ステータスバッジ */}
        <Badge position="absolute" top={2} right={2} colorPalette={getStatusColor()} fontSize="xs">
          {getStatusLabel()}
        </Badge>

        {/* ワクチン期限警告バッジ */}
        {alertLevel && alertLevel !== 'none' && (
          <Badge
            position="absolute"
            top={2}
            left={2}
            colorPalette={getAlertColor(alertLevel)}
            fontSize="xs"
          >
            ワクチン{getAlertLabel(alertLevel)}
          </Badge>
        )}
      </Box>

      {/* カード情報 */}
      <Box p={4}>
        <VStack align="stretch" gap={2}>
          {/* 名前（正式名称 + どうぶつ名 + 登録番号）と削除ボタン */}
          <HStack justify="space-between" align="flex-start">
            <Box
              flex={1}
              minW={0}
              cursor="pointer"
              onClick={() => navigate(`/dogs/${dog.id}`)}
            >
              <DogIdentityLabel dog={dog} size="md" showRegistrationNumbers />
            </Box>
            <IconButton
              aria-label="削除"
              size="xs"
              variant="ghost"
              colorPalette="red"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(dog);
              }}
            >
              <FiTrash2 />
            </IconButton>
          </HStack>

          {/* 性別と年齢 */}
          <HStack gap={2} cursor="pointer" onClick={() => navigate(`/dogs/${dog.id}`)}>
            <Badge colorPalette={getGenderColor()} fontSize="xs">
              {dog.gender === 'male' ? 'オス' : 'メス'}
            </Badge>
            <Text fontSize="sm" color="gray.600">
              {calculateAge()}
            </Text>
          </HStack>

          {/* 出生日 */}
          <Text fontSize="xs" color="gray.400" cursor="pointer" onClick={() => navigate(`/dogs/${dog.id}`)}>
            {format(dog.birthDate.toDate(), 'yyyy年M月d日生', { locale: ja })}
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

/**
 * 個体一覧ページコンポーネント
 */
const DogListPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { dogs, loading, error, fetchDogs, searchDogs, setSearchFilter, deleteDog } = useDogStore();
  const { upcomingVaccinations } = useVaccinationStore();
  const [activeTab, setActiveTab] = useState<'all' | 'female' | 'male'>('all');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [dogToDelete, setDogToDelete] = useState<Dog | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (user && !isSearchMode) {
      loadDogs();
    }
  }, [user, activeTab, isSearchMode]);

  /**
   * 個体一覧読み込み
   */
  const loadDogs = () => {
    if (!user) return;

    let gender: Gender | undefined;
    if (activeTab === 'female') {
      gender = 'female';
    } else if (activeTab === 'male') {
      gender = 'male';
    }

    fetchDogs(user.uid, gender);
  };

  /**
   * タブ変更時の処理
   */
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'all' | 'female' | 'male');
    setIsSearchMode(false); // タブ変更時は検索モードを解除
  };

  /**
   * 新規登録ボタンクリック
   */
  const handleNewDog = () => {
    navigate('/dogs/new');
  };

  /**
   * 検索実行
   */
  const handleSearch = (filter: DogSearchFilter) => {
    if (!user) return;

    // タブで選択されている性別フィルターを追加
    if (activeTab === 'female') {
      filter.gender = 'female';
    } else if (activeTab === 'male') {
      filter.gender = 'male';
    }

    // フィルターをストアに設定してから検索実行
    setSearchFilter(filter);
    searchDogs(user.uid);
    setIsSearchMode(true);
  };

  /**
   * 検索クリア
   */
  const handleClearSearch = () => {
    if (!user) return;
    setSearchFilter({});
    setIsSearchMode(false);
    loadDogs();
  };

  /**
   * 削除ダイアログを開く
   */
  const handleDeleteClick = (dog: Dog) => {
    setDogToDelete(dog);
    setIsDeleteDialogOpen(true);
  };

  /**
   * 個体削除実行
   */
  const handleDelete = async (permanent: boolean) => {
    if (!user || !dogToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDog(dogToDelete.id, user.uid, permanent);
      toaster.success({
        title: permanent ? '個体を完全に削除しました' : 'ステータスを「死亡」に変更しました',
      });
    } catch (error) {
      console.error('削除エラー:', error);
      toaster.error({
        title: '削除に失敗しました',
        description: error instanceof Error ? error.message : '不明なエラーが発生しました',
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDogToDelete(null);
    }
  };

  // ワクチン期限アラートのレベルを個体IDごとにマッピング（overdue を優先）
  const alertLevelByDogId = new Map<string, VaccinationAlertLevel>();
  upcomingVaccinations.forEach((record) => {
    if (!record.nextDueDate) return;
    const { level } = getAlertLevelFromDueDate(record.nextDueDate.toDate());
    if (level === 'none') return;
    const current = alertLevelByDogId.get(record.dogId);
    if (current === 'overdue') return;
    if (level === 'overdue' || !current) {
      alertLevelByDogId.set(record.dogId, level);
    }
  });

  // 検索中にフォームが再マウントされて入力値が消えないよう、
  // 全画面ローディングへ早期 return せず、カード一覧部分のみで状態表示する。
  return (
    <AppLayout>
      <Box maxW="6xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <Heading size="xl">個体管理</Heading>
            <HStack gap={2}>
              <Button colorPalette="blue" onClick={handleNewDog}>
                + 新規登録
              </Button>
            </HStack>
          </HStack>

          {/* エラー表示 */}
          {error && (
            <Box p={4} bg="red.50" borderRadius="md" color="red.700">
              <Text fontSize="sm">{error}</Text>
            </Box>
          )}

          {/* ワクチン期限アラートセクション */}
          {user && <VaccinationAlertSection userId={user.uid} dogs={dogs} />}

          {/* タブ */}
          <Tabs.Root value={activeTab} onValueChange={(e) => handleTabChange(e.value as string)}>
            <Tabs.List>
              <Tabs.Trigger value="all">全体一覧</Tabs.Trigger>
              <Tabs.Trigger value="female">メス犬</Tabs.Trigger>
              <Tabs.Trigger value="male">オス犬</Tabs.Trigger>
            </Tabs.List>
          </Tabs.Root>

          {/* 検索フォーム */}
          <DogSearchForm onSearch={handleSearch} onClear={handleClearSearch} loading={loading} />

          {/* 検索結果数 */}
          <HStack justify="space-between">
            <Text color="gray.600" fontSize="sm">
              {isSearchMode ? '検索結果' : '全件表示'}: {dogs.length}件
            </Text>
          </HStack>

          {/* 個体カード一覧 */}
          {loading && dogs.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={10}>
              読み込み中...
            </Text>
          ) : dogs.length === 0 ? (
            <Box
              p={10}
              textAlign="center"
              bg="gray.50"
              borderRadius="lg"
              border="2px dashed"
              borderColor="gray.200"
            >
              <VStack gap={4}>
                <Text fontSize="lg" color="gray.600">
                  {isSearchMode
                    ? '条件に一致する個体がありません'
                    : '登録されている個体がありません'}
                </Text>
                {!isSearchMode && (
                  <Button colorPalette="blue" onClick={handleNewDog}>
                    最初の個体を登録する
                  </Button>
                )}
              </VStack>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
              {dogs.map((dog) => (
                <DogCardWithAI
                  key={dog.id}
                  dog={dog}
                  onDeleteClick={handleDeleteClick}
                  alertLevel={alertLevelByDogId.get(dog.id)}
                />
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Box>

      {/* 削除確認ダイアログ */}
      {dogToDelete && (
        <DeleteDogDialog
          dog={dogToDelete}
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open);
            if (!open) setDogToDelete(null);
          }}
          onDelete={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </AppLayout>
  );
};

export default DogListPage;
